import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';

export interface ToggleableListItem {
    key: string;
    label: string;
    description?: string;
}

export interface ToggleableListGroup {
    key: string;
    label: string;
    description?: string;
    items: ToggleableListItem[];
    /** Keys of items currently enrolled in this group. */
    selected: string[];
}

interface ToggleableListProps {
    groups: ToggleableListGroup[];
    /**
     * Persist a single toggle. Resolve on success; throw to roll the
     * checkbox back (a destructive toast is shown automatically).
     */
    onToggle: (
        groupKey: string,
        itemKey: string,
        enabled: boolean
    ) => Promise<void>;
    disabled?: boolean;
    emptyText?: string;
    /** Label used in the count pill, e.g. "enrolled". */
    countLabel?: string;
    /** Group keys expanded on first render. */
    defaultOpen?: string[];
}

/**
 * A list of collapsible groups, each with a checkbox per item. Every
 * toggle saves immediately (optimistic, rolled back on failure).
 */
export default function ToggleableList({
    groups,
    onToggle,
    disabled = false,
    emptyText = 'Nothing to show',
    countLabel = 'enrolled',
    defaultOpen = [],
}: ToggleableListProps) {
    const { toast } = useToast();
    const [pending, setPending] = useState<Set<string>>(new Set());
    // Optimistic overrides keyed by `${group}:${item}` until the save settles.
    const [overrides, setOverrides] = useState<Record<string, boolean>>({});

    if (groups.length === 0) {
        return <p className="text-sm text-muted-foreground">{emptyText}</p>;
    }

    const cellKey = (g: string, i: string) => `${g}:${i}`;

    const isChecked = (group: ToggleableListGroup, itemKey: string) => {
        const k = cellKey(group.key, itemKey);
        return k in overrides
            ? overrides[k]
            : group.selected.includes(itemKey);
    };

    const enrolledCount = (group: ToggleableListGroup) =>
        group.items.filter((item) => isChecked(group, item.key)).length;

    const toggle = async (group: ToggleableListGroup, itemKey: string) => {
        const k = cellKey(group.key, itemKey);
        if (disabled || pending.has(k)) return;
        const next = !isChecked(group, itemKey);

        setOverrides((prev) => ({ ...prev, [k]: next }));
        setPending((prev) => new Set(prev).add(k));
        try {
            await onToggle(group.key, itemKey, next);
        } catch {
            setOverrides((prev) => ({ ...prev, [k]: !next }));
            toast({ title: 'Failed to save change', variant: 'destructive' });
        } finally {
            setPending((prev) => {
                const copy = new Set(prev);
                copy.delete(k);
                return copy;
            });
        }
    };

    return (
        <AccordionPrimitive.Root
            type="multiple"
            defaultValue={defaultOpen}
            className="flex flex-col gap-3"
        >
            {groups.map((group) => (
                <AccordionPrimitive.Item
                    key={group.key}
                    value={group.key}
                    className="rounded-lg border border-subtle bg-card"
                >
                    <AccordionPrimitive.Header>
                        <AccordionPrimitive.Trigger className="group flex w-full items-center gap-3 px-4 py-3 text-left">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-foreground">
                                        {group.label}
                                    </span>
                                    <span className="rounded-full bg-zinc-300 px-2 py-0.5 text-xs font-medium tabular-nums text-zinc-800">
                                        {enrolledCount(group)} /{' '}
                                        {group.items.length} {countLabel}
                                    </span>
                                </div>
                                {group.description && (
                                    <p className="mt-0.5 text-sm text-muted-foreground">
                                        {group.description}
                                    </p>
                                )}
                            </div>
                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                        </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>
                    <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                        <div className="flex flex-col gap-1.5 px-4 pb-4">
                            {group.items.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    {emptyText}
                                </p>
                            ) : (
                                group.items.map((item) => {
                                    const busy = pending.has(
                                        cellKey(group.key, item.key)
                                    );
                                    const inactive = disabled || busy;
                                    return (
                                        <label
                                            key={item.key}
                                            className={`flex items-center gap-2.5 rounded-md border border-white/[0.06] bg-white/[0.03] px-3 py-2 text-sm ${
                                                inactive
                                                    ? 'opacity-60'
                                                    : 'cursor-pointer hover:bg-white/[0.05]'
                                            }`}
                                        >
                                            <Checkbox
                                                checked={isChecked(
                                                    group,
                                                    item.key
                                                )}
                                                onCheckedChange={() =>
                                                    toggle(group, item.key)
                                                }
                                                disabled={inactive}
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-foreground">
                                                    {item.label}
                                                </span>
                                                {item.description && (
                                                    <span className="block truncate text-xs text-muted-foreground">
                                                        {item.description}
                                                    </span>
                                                )}
                                            </span>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
            ))}
        </AccordionPrimitive.Root>
    );
}
